import Calendar from '@/components/calendar/Calendar';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input, PasswordInput } from '@/components/common/Input';
import ImageUploadAccount from '@/components/general/ImageUploadAccount';
import { DATE_YMD_2, ekDomain, EMAIL_VALIDATE_REGEX, LICENSE_TYPE_OPTIONS, MAX_LENGTH_NUMBER, NUMBER_FULL_HALF_VALIDATE_REGEX, PASSWORD_VALIDATE_REGEX, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { formatDateTime } from '@/utils/datetime';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Checkbox } from '@/components/common/Checkbox';
import { isPathEk } from '@/utils/routes';
import StepProgressBar from '@/components/general/step-indicator';
import { Textarea } from '@/components/common/Textarea';
import ExampleHelp from './components/ExampleHelp';
import Combobox from '@/components/common/Combobox';
import { replaceSpaces } from '@/utils/utils';
import AnimateButton from '@/components/general/button-custom/AnimateButton';
import { convertFullToHalf } from '@/utils/formatNumber';
import { LICENSE_TYPE_OPTION } from '@/types/enums';
import { RadioGroup, RadioGroupItem } from '@/components/common/RadioGroup';
import { Label } from '@/components/common/Label';

export default observer(function RegisterStep2GasPage() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid') ?? '';
    const emailSearchParams = searchParams.get('email') ?? '';
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        authStore: { registerRD, checkUuid },
        generalStore: { licenseTypeGas, getLicenseGas }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        licenseType: yup
            .string()
            .required('form_error_validate.required'),
        licenseTypeOption: yup
            .number()
            .required('form_error_validate.required'),
        licenseBeforeImageId: yup
            .number()
            .required('form_error_validate.required'),
        licenseAfterImageId: yup
            .number()
            .when('licenseTypeOption', {
                is: licenseTypeOption => Number(licenseTypeOption) !== LICENSE_TYPE_OPTION.MY_NUMBER,
                then: _ => yup.number().required('form_error_validate.required'),
                otherwise: _ => yup.number().optional()
            }),
        licenseExporeDate: yup
            .date(),
        fullname: yup
            .string()
            .required('form_error_validate.required'),
        licenseNumber: yup
            .string()
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        birthOfDate: yup
            .date()
            .required('form_error_validate.required'),
        zipCode: yup
            .string()
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        address: yup
            .string(),
        eRegister: yup
            .string()
            .required('form_error_validate.required')
            .matches(EMAIL_VALIDATE_REGEX, 'form_error_validate.email'),
        phone: yup
            .string()
            .required('form_error_validate.required')
            .matches(NUMBER_FULL_HALF_VALIDATE_REGEX, 'form_error_validate.only_number'),
        furiganaName: yup
            .string(),
        passwordRegister: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .matches(PASSWORD_VALIDATE_REGEX, 'form_error_validate.format_password'),
        confirmPassword: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .matches(PASSWORD_VALIDATE_REGEX, 'form_error_validate.format_password')
            .oneOf([yup.ref('passwordRegister')], 'form_error_validate.password_not_match'),
        note: yup
            .string(),
        agree: yup
            .boolean(),
        agreeConfirmed: yup
            .boolean()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            licenseType: '',
            licenseTypeOption: LICENSE_TYPE_OPTION.TRADITIONAL,
            licenseBeforeImageId: undefined,
            licenseAfterImageId: undefined,
            licenseExporeDate: undefined,
            fullname: '',
            licenseNumber: '',
            birthOfDate: undefined,
            zipCode: '',
            address: '',
            eRegister: '',
            phone: '',
            furiganaName: '',
            passwordRegister: '',
            confirmPassword: '',
            note: '',
            agree: false,
            agreeConfirmed: false
        }
    });

    // form watch
    const watchAgree = form.watch('agree');
    const watchAgreeConfirmed = form.watch('agreeConfirmed');
    const watchLicenseTypeOption = form.watch('licenseTypeOption');

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    const useMyNumberLicense = useMemo((): boolean => {
        return Number(watchLicenseTypeOption) === LICENSE_TYPE_OPTION.MY_NUMBER;
    }, [watchLicenseTypeOption]);

    // lifecycle
    useEffect(() => {
        getLicenses();
    }, []);

    useEffect(() => {
        form.setValue('eRegister', emailSearchParams || '');
    }, [emailSearchParams]);

    // function
    const getLicenses = async () => {
        await flowResult(getLicenseGas());
    };

    const onSubmit = async (data: FormData) => {
        const {
            licenseType,
            licenseTypeOption,
            licenseBeforeImageId,
            licenseAfterImageId,
            licenseExporeDate,
            fullname,
            licenseNumber,
            birthOfDate,
            zipCode,
            address,
            eRegister,
            phone,
            furiganaName,
            passwordRegister,
            confirmPassword,
            note
        } = data;

        const dataSubmit = {
            licenseType: licenseType && !isNaN(Number(licenseType)) ? [Number(licenseType)] : [],
            licenseTypeOption: licenseTypeOption || null,
            licenseBeforeImageId: licenseBeforeImageId || null,
            licenseAfterImageId: (!useMyNumberLicense && licenseAfterImageId) ? licenseAfterImageId : null,
            licenseExporeDate: formatDateTime((licenseExporeDate || ''), DATE_YMD_2),
            fullname,
            licenseNumber: licenseNumber ? convertFullToHalf(licenseNumber) : '',
            birthOfDate: formatDateTime((birthOfDate || ''), DATE_YMD_2),
            zipCode: zipCode ? convertFullToHalf(zipCode) : '',
            address,
            email: eRegister,
            phone: phone ? convertFullToHalf(phone) : '',
            furiganaName: furiganaName,
            password: passwordRegister,
            confirmPassword,
            note
        };

        const checkUuidRes = await flowResult(checkUuid({ uuid }));
        if (checkUuidRes) {
            const res = await flowResult(registerRD(dataSubmit));
            if (res && res?.ok) {
                navigate(ROUTES.signup3.href, {
                    state: {
                        email: eRegister
                    }
                });
            }
        }
    };

    const uploadSuccessBefore = (value) => {
        if (useMyNumberLicense) {
            return;
        }

        form.setValue('licenseBeforeImageId', value?.licenseImage?.id || null);

        form.setValue('fullname', value?.licenseInfo?.name ? replaceSpaces(value?.licenseInfo?.name) : '');
        value?.licenseInfo?.dateOfBirth ?
            form.setValue('birthOfDate', new Date(value?.licenseInfo?.dateOfBirth)) :
            form.resetField('birthOfDate');
        value?.licenseInfo?.dateOfExpiration ?
            form.setValue('licenseExporeDate', new Date(value?.licenseInfo?.dateOfExpiration)) :
            form.resetField('licenseExporeDate');
        form.setValue('licenseNumber', value?.licenseInfo?.licenseNumber ? replaceSpaces(value?.licenseInfo?.licenseNumber) : '');
        form.setValue('address', value?.licenseInfo?.address ? replaceSpaces(value?.licenseInfo?.address) : '');
    };

    return (
        <div>
            <h3 className='title-1 text-center mb-3'>{t('words_title.register_account')}</h3>
            <div className='pt-6'>
                <StepProgressBar
                    startingStep={1}
                    wrapperClass='w-full'
                    steps={[
                        {
                            label: t('words_title.register_page.step_1'),
                            name: 'register_page.step_1'
                        },
                        {
                            label: t('words_title.register_page.step_2'),
                            name: 'register_page.step_2'
                        },
                        {
                            label: t('words_title.register_page.step_3'),
                            name: 'register_page.step_3'
                        }
                    ]}
                />
            </div>
            <div className='h-8 w-full'></div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='mt-3' autoComplete='off'>
                    <div>
                        <h2 className='title-2'>{t('words_title.user_infomation')}</h2>
                        <div className='grid grid-cols-1 mt-4 gap-4'>
                            <FormField
                                control={form.control}
                                name='licenseType'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.driver_license_type')}</FormLabel>
                                        <FormControl>
                                            <Combobox
                                                value={field.value?.toString() ?? ''}
                                                onChange={(item) => {
                                                    field.onChange(item?.code);
                                                    form.setValue('licenseType', item?.code);
                                                }}
                                                items={
                                                    licenseTypeGas.map(item => ({
                                                        label: item.name,
                                                        value: item.id?.toString(),
                                                        code: item.id
                                                    }))
                                                }
                                                search={false}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name='licenseTypeOption'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormControl>
                                            <div className='flex items-center min-h-[20px]'>
                                                <RadioGroup
                                                    key={field.value}
                                                    onValueChange={(value) => {
                                                        field.onChange(value);
                                                        form.clearErrors('licenseBeforeImageId');
                                                        form.clearErrors('licenseAfterImageId');
                                                    }}
                                                    value={field.value?.toString()}
                                                    className='flex gap-6 w-full'
                                                >
                                                    {LICENSE_TYPE_OPTIONS.map((option, index) => (
                                                        <div
                                                            key={index}
                                                            className='flex items-center space-x-2'
                                                        >
                                                            <RadioGroupItem
                                                                value={option.value.toString()}
                                                                id={option.value.toString()}
                                                            />
                                                            <Label className='cursor-pointer font-normal' htmlFor={option.value.toString()}>
                                                                {option.name}
                                                            </Label>
                                                        </div>
                                                    ))}
                                                </RadioGroup>
                                            </div>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className='my-6'>
                            <ExampleHelp />
                        </div>

                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6'>
                            <div>
                                <ImageUploadAccount
                                    form={form}
                                    filedName='licenseBeforeImageId'
                                    caption={useMyNumberLicense ? t('field.myna_license') : t('words_title.before')}
                                    isUploadOCR={!useMyNumberLicense}
                                    className='h-56'
                                    onUploadSuccess={uploadSuccessBefore}
                                    isOcrNoToken={true}
                                    required={true}
                                />
                            </div>
                            {!useMyNumberLicense && (
                                <div>
                                    <ImageUploadAccount
                                        filedName='licenseAfterImageId'
                                        form={form}
                                        caption={t('words_title.after')}
                                        className='h-56'
                                        required={true}
                                    />
                                </div>
                            )}
                        </div>

                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 '>
                            <FormField
                                control={form.control}
                                name='licenseExporeDate'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel>{t('field.license_expire_date')}</FormLabel>
                                        <FormControl>
                                            <Calendar
                                                mode='single'
                                                selected={field.value}
                                                onSelect={date => field.onChange(date)}
                                                useIconRemove={false}
                                                required={false}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='fullname'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.name_or_company_name')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='furiganaName'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('field.furigana_name')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                autoComplete='off'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='licenseNumber'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('field.driver_license_number')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                isHalfWidth
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='birthOfDate'
                                render={({ field, fieldState }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.date_of_birth')}</FormLabel>
                                        <FormControl>
                                            <Calendar
                                                mode='single'
                                                selected={field.value}
                                                onSelect={date => field.onChange(date)}
                                                useIconRemove={false}
                                                required={false}
                                                isInvalid={!!fieldState.error}
                                                disabled={[
                                                    {
                                                        after: new Date()
                                                    }
                                                ]}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='zipCode'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel>{t('field.post_code')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                isHalfWidth
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='address'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel>{t('field.address')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                    </div>

                    <div className='mt-4'>
                        <div className='flex flex-col gap-4'>
                            <FormField
                                control={form.control}
                                name='eRegister'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.email')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                disabled={true}
                                                autoComplete='off'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='phone'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.phone_number')}</FormLabel>
                                        <FormControl>
                                            <Input
                                                type='text'
                                                placeholder={t('placeholder.enter')}
                                                maxLength={MAX_LENGTH_NUMBER}
                                                autoComplete='new-phone'
                                                isHalfWidth
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='passwordRegister'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.password')}</FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                type={!showPassword ? 'password' : 'text'}
                                                placeholder={t('placeholder.enter')}
                                                onToggleIcon={() => setShowPassword(!showPassword)}
                                                autoComplete='new-password'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='confirmPassword'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>{t('field.confirm_password')}</FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                type={!showConfirmPassword ? 'password' : 'text'}
                                                placeholder={t('placeholder.enter')}
                                                onToggleIcon={() => setShowConfirmPassword(!showConfirmPassword)}
                                                autoComplete='off'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='note'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel>{t('field.note')}</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder={t('sentences.register_note_description')}
                                                className='min-h-28'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>

                    <div className='mt-4 flex flex-col gap-4'>
                        <FormField
                            control={form.control}
                            name='agree'
                            render={({ field }) => (
                                <FormItem className='flex items-center'>
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                    <FormLabel className='ml-1.5 mb-0 cursor-pointer font-normal'>
                                        <Trans
                                            i18nKey='sentences.agree_register_1'
                                            components={{
                                                Link: (
                                                    <Link
                                                        to={isEk ? `/${ekDomain}/${ROUTES.privacyPolicy.hrefEK}` : ROUTES.privacyPolicy.href}
                                                        target='_blank'
                                                        className='link-1'
                                                    />
                                                )
                                            }}
                                        />
                                    </FormLabel>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='agreeConfirmed'
                            render={({ field }) => (
                                <FormItem className='flex items-center'>
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                    <FormLabel className='ml-1.5 mb-0 cursor-pointer font-normal'>
                                        {t('sentences.confirmed_info_register')}
                                    </FormLabel>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div>
                            <span className='before:content-["_*"] before::inline-block before:text-red-700'>
                                {t('sentences.note_register')}
                            </span>
                        </div>
                    </div>

                    <div className='flex justify-center'>
                        <AnimateButton
                            contentComp={t('button.send')}
                            parentClassName='mt-8 w-[208px] h-[48px]'
                            disabled={watchAgree !== true || watchAgreeConfirmed !== true}
                            type='submit'
                            outline
                        >
                        </AnimateButton>
                    </div>

                </form>
            </Form>
        </div>
    );
});
